import { useState } from 'react';
import { Box, Chip, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { registerGamVersion } from '../../../api/gam';
import { GAM_KEYS } from '../../../core/constants';
import { KEY_PATTERN, metricCodeStyles, RESULT_LABELS, RESULT_COLORS } from './constants';

export default function GamKeyUpdate() {
	const [key, setKey] = useState(GAM_KEYS.INSTREAM_VARIATION);
	const [version, setVersion] = useState('');
	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState(null);
	const trimmedKey = key.trim();
	const trimmedVersion = version.trim();
	const keyError = !!trimmedKey && (trimmedKey.length > 127 || !KEY_PATTERN.test(trimmedKey));
	const versionError = !!version && (trimmedVersion.length < 5 || trimmedVersion.length > 40);

	const handleUpdate = async (event) => {
		event.preventDefault();
		if (loading || !trimmedKey || !trimmedVersion || keyError || versionError) return;

		setLoading(true);
		setResult(null);
		try {
			const { data } = await registerGamVersion(trimmedKey, trimmedVersion);
			setResult(data.data);
			toast.success('GAM key update completed', { position: toast.POSITION.BOTTOM_RIGHT });
		} catch (error) {
			const failureResult = error?.response?.data?.data;
			if (Array.isArray(failureResult?.results)) setResult(failureResult);
			const errorMessage = error?.response?.data?.data?.message ||
				(typeof error?.response?.data?.data === 'string' ? error.response.data.data : null) ||
				error.message;
			toast.error(errorMessage, { position: toast.POSITION.BOTTOM_RIGHT });
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box sx={{ width: '100%', maxWidth: 860, mx: 'auto', py: { xs: 2, md: 4 } }}>
			<Paper variant="outlined" sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
				<Typography variant="h6" fontWeight={600} gutterBottom>
					Register a GAM targeting value
				</Typography>
				<Typography color="text.secondary" sx={{ mb: 3 }}>
					Registers the exact version under this key in each configured GAM network if missing and reactivates it if inactive.
				</Typography>
				<Box component="form" onSubmit={handleUpdate}>
					<Stack spacing={3}>
						<TextField
							label="Key"
							value={key}
							onChange={(event) => { setKey(event.target.value); setResult(null); }}
							error={keyError}
							helperText={keyError ? 'Use up to 127 letters, numbers, or underscores.' : undefined}
							inputProps={{ maxLength: 127 }}
							disabled={loading}
							fullWidth
						/>
						<TextField
							label="Value"
							placeholder="x.y.z-suffix or x.y.z_suffix"
							InputLabelProps={{ shrink: true }}
							value={version}
							onChange={(event) => { setVersion(event.target.value); setResult(null); }}
							error={versionError}
							helperText={versionError
								? 'Enter at least 5 characters after trimming surrounding whitespace.'
								: 'Any value with at least 5 characters after trimming surrounding whitespace.'}
							inputProps={{ maxLength: 40, minLength: 5 }}
							disabled={loading}
							fullWidth
						/>
						<LoadingButton
							type="submit"
							variant="contained"
							loading={loading}
							disabled={!trimmedKey || !trimmedVersion || keyError || versionError}
							sx={{ alignSelf: 'flex-start', minWidth: 120 }}
						>
							Update
						</LoadingButton>
					</Stack>
				</Box>
			</Paper>

			{result && (
				<Paper variant="outlined" sx={{ p: { xs: 2, md: 4 }, borderRadius: 2, mt: 3 }} aria-live="polite">
					<Typography variant="h6" fontWeight={600}>Update results</Typography>
					<Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
						<Box component="code" sx={metricCodeStyles}>{result.requestedVersion}</Box>
						{' · '}{result.key}{' · '}
						<Box component="code" sx={metricCodeStyles}>{result.networkCount}</Box> networks
					</Typography>
					<Divider sx={{ borderColor: 'black', borderWidth: 0.7 }} />
					{result.results.map((network) => (
						<Box
							key={network.networkCode}
							sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}
						>
							<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
								<Typography fontWeight={600}>
									Network <Box component="code" sx={metricCodeStyles}>{network.networkCode}</Box>
								</Typography>
								<Chip
									label={RESULT_LABELS[network.action] || network.action}
									color={RESULT_COLORS[network.action] || 'default'}
									size="small"
								/>
							</Stack>
							{network.message && (
								<Typography variant="body2" color={network.action === 'failed' ? 'error' : 'text.secondary'} sx={{ mt: 0.5 }}>
									{network.message}
								</Typography>
							)}
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Key ID: <Box component="code" sx={metricCodeStyles}>{network.keyId || 'Unavailable'}</Box>
								{' | '}
								<Box component="code" sx={metricCodeStyles}>
									{network.registeredValueCount ?? 'Unknown'}
								</Box> values before update
								{network.registeredValue?.id && (
									<>
										{' | '}Value ID:{' '}
										<Box component="code" sx={metricCodeStyles}>
											{network.registeredValue.id}
										</Box>
									</>
								)}
							</Typography>
						</Box>
					))}
				</Paper>
			)}
		</Box>
	);
}
